"use client";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

function formatOddsDisplay(odds: number): string {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

export function OddsBadge({
  odds,
  variant = "default",
  highlight = false,
  betLink,
  onBetLinkClick,
  showTooltip = false,
  display,
  ariaLabel,
  className,
  tooltipClassName,
}: {
  odds: number;
  variant?: "default" | "small";
  highlight?: boolean;
  betLink?: string;
  onBetLinkClick?: () => void;
  showTooltip?: boolean;
  display?: string;
  ariaLabel?: string;
  className?: string;
  tooltipClassName?: string;
}) {
  const sizeClass = variant === "small" ? "px-1.5 py-0.5 text-xs" : "px-2 py-1 text-sm";
  const oddsDisplay = display ?? formatOddsDisplay(odds);

  const baseClasses = cn(
    "inline-flex items-center rounded-md font-semibold data-num",
    sizeClass,
    highlight ? "bg-accent-value text-background" : "bg-white/[0.06] text-foreground/90"
  );

  if (betLink) {
    const link = (
      <a
        href={betLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          onBetLinkClick?.();
        }}
        aria-label={ariaLabel}
        className={cn(
          baseClasses,
          "cursor-pointer transition-colors duration-150",
          highlight ? "hover:bg-accent-value-strong" : "hover:bg-white/[0.12]",
          className,
        )}
      >
        {oddsDisplay}
      </a>
    );

    if (showTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {link}
          </TooltipTrigger>
          <TooltipContent side="top" className={cn("text-xs", tooltipClassName)}>
            Open at the book
          </TooltipContent>
        </Tooltip>
      );
    }

    return link;
  }

  return (
    <span className={cn(baseClasses, className)}>
      {oddsDisplay}
    </span>
  );
}
